/**
 * dynamicImageUtils — Shared helpers for per-recipient AI image generation,
 * used by both Normal mode and Bulk mode.
 *
 * Flow (mirrors the static {{image:token}} pipeline):
 *   1. `collectAiImageTokens(body, imageLabAssets)` finds AI tokens in the body.
 *   2. `generateAiImageForContact(...)` calls the `generateDynamicImage` cloud
 *      function → returns a hosted Storage URL under `email_body_images/`.
 *   3. The caller keeps a token→url override map and merges it into
 *      `userSettings.imageLabAssets` at send time, so `convertToHtml` /
 *      `resolveImageTokens` and the backend CID inliner work unchanged.
 */

import { authenticatedFetch, CLOUD_FUNCTION_URLS } from "../../utils/cloudFunctions";
import { getPrimaryDecisionMaker, getFirstDmName, getRecipientInfoForContact } from "../../shared/enums";

const IMAGE_TOKEN_REGEX = /\{\{\s*image:([a-zA-Z0-9_-]+)\s*\}\}/g;

/**
 * Concurrency gate for AI image generation.
 *
 * One generation keeps a `generateDynamicImage` instance busy for 1–3 minutes
 * (Gen 1, concurrency 1, 1 GB), so an unbounded fan-out — Bulk mode's
 * look-ahead prefetch times the image tokens per mail — would spawn one
 * instance per image and run into the provider's rate limit. The gate caps how
 * many run at once and always serves the contact the user is looking at before
 * any prefetch work.
 */
const MAX_CONCURRENT_IMAGE_GENERATIONS = 5;

/** Warming an upcoming contact — yields to anything the user is waiting for. */
export const IMAGE_PRIORITY_PREFETCH = 0;
/** The contact on screen: goes to the front of the queue. */
export const IMAGE_PRIORITY_ACTIVE = 1;

let activeImageGenerations = 0;
const imageSlotQueue = [];

const grantNextImageSlot = () => {
  if (activeImageGenerations >= MAX_CONCURRENT_IMAGE_GENERATIONS) return;
  if (imageSlotQueue.length === 0) return;
  // Highest priority wins; FIFO within the same priority.
  let bestIdx = 0;
  for (let i = 1; i < imageSlotQueue.length; i++) {
    if (imageSlotQueue[i].priority > imageSlotQueue[bestIdx].priority) bestIdx = i;
  }
  const [next] = imageSlotQueue.splice(bestIdx, 1);
  activeImageGenerations += 1;
  next.resolve();
};

const acquireImageSlot = (priority, key) =>
  new Promise((resolve) => {
    imageSlotQueue.push({ priority, key, resolve });
    grantNextImageSlot();
  });

const releaseImageSlot = () => {
  activeImageGenerations = Math.max(0, activeImageGenerations - 1);
  grantNextImageSlot();
};

const imageJobKey = (contactId, token) => `${contactId || ""}::${token}`;

/**
 * Raise a still-queued generation to active priority. Needed when the user
 * navigates to a contact that was already queued as a low-priority prefetch —
 * without this it would keep yielding to other prefetch jobs while the user
 * waits for it. No-op once the job is running.
 */
export const promoteImageGeneration = (contactId, token) => {
  const key = imageJobKey(contactId, token);
  let promoted = false;
  imageSlotQueue.forEach((entry) => {
    if (entry.key === key && entry.priority < IMAGE_PRIORITY_ACTIVE) {
      entry.priority = IMAGE_PRIORITY_ACTIVE;
      promoted = true;
    }
  });
  return promoted;
};

/** True for the error thrown when a queued generation was dropped unstarted. */
export const isSkippedImageError = (err) => !!(err && err.imageSkipped);

/**
 * Find dynamic (type:"ai") image tokens present in the email body.
 * @param {string} body - plaintext email body (may contain {{image:token}})
 * @param {Object} imageLabAssets - token -> asset map (from userSettings)
 * @returns {Array<{token: string, asset: Object}>}
 */
export const collectAiImageTokens = (body, imageLabAssets) => {
  if (!body || typeof body !== "string") return [];
  const assets = imageLabAssets && typeof imageLabAssets === "object" ? imageLabAssets : {};
  const found = new Map();
  let m;
  IMAGE_TOKEN_REGEX.lastIndex = 0;
  while ((m = IMAGE_TOKEN_REGEX.exec(body)) !== null) {
    const token = m[1];
    const asset = assets[token];
    if (asset && (asset.type === "ai" || asset.type === "miniApp") && !found.has(token)) {
      found.set(token, { token, asset });
    }
  }
  return Array.from(found.values());
};

/**
 * Build the flat {{variable}} substitution map for an image prompt from a
 * contact/lead row. Keep keys aligned with the template variable convention.
 */
/**
 * Build the full {{variable}} substitution map for an image prompt from a
 * contact/lead row. Keys are IDENTICAL to the text-template variable catalog
 * (WorldSettingsTopics) and resolve exactly like NormalMode's replacePlaceholders,
 * so any {{variable}} that works in a template also works in an image prompt.
 *
 * @param {Object} contact - lead/contact row
 * @param {Object} [opts]
 * @param {Object} [opts.userSettings] - for {{user_name}} / {{company_name}}
 * @param {string} [opts.selectedEmail] - for {{recipient_name}} / {{recipient_role}}
 */
export const buildImagePromptVariables = (contact, opts = {}) => {
  if (!contact || typeof contact !== "object") return {};
  const userSettings = opts.userSettings || {};
  const selectedEmail = opts.selectedEmail || null;

  // Decision-maker / recipient resolution (same helpers as the send pipeline).
  const dmFirstName = getFirstDmName(contact);
  const primaryDm = getPrimaryDecisionMaker(contact);
  let dmFullName = "";
  let dmLastName = contact.lead_lastname || "";
  if (primaryDm && primaryDm.name) {
    dmFullName = primaryDm.name;
    const parts = primaryDm.name.split(" ");
    if (parts.length > 1) dmLastName = parts.slice(1).join(" ");
  }
  const recipient = getRecipientInfoForContact(contact, selectedEmail) || { name: "", role: "" };

  // SEO variables — mirror replacePlaceholders() exactly.
  const seo = contact.researchIntelligence?.seoAnalysis;
  const d = seo?.data || {};
  const seoVisibility = d.core?.visibility?.value != null ? Number(d.core.visibility.value).toFixed(2) : "";
  const seoTopKeyword = Array.isArray(d.opportunities) && d.opportunities[0] ? d.opportunities[0].keyword : "";
  const seoMainCompetitor = Array.isArray(d.competitors) && d.competitors[0] ? d.competitors[0].domain : "";
  const seoBacklinks = d.backlinks?.total != null ? String(d.backlinks.total) : "";
  const seoKeywordsTotal = d.core?.keywordsTotal != null ? String(d.core.keywordsTotal) : "";
  const seoKeywordsTop10 = d.core?.keywordsTop10 != null ? String(d.core.keywordsTop10) : "";
  const seoTraffic = d.trafficValue?.estimatedTrafficMonthly != null ? String(d.trafficValue.estimatedTrafficMonthly) : "";
  const seoTrafficValue = d.trafficValue?.estimatedValueMonthly ? `${d.trafficValue.estimatedValueMonthly} €` : "";
  const vt = d.visibilityTrend;
  const seoTrend = vt?.changePct != null ? `${vt.changePct > 0 ? "+" : ""}${vt.changePct}% (${vt.overWindow || "12M"})` : "";

  return {
    // Company / lead fields
    business_name: contact.business_name || "",
    lead_name: dmFullName || contact.lead_name || "",
    lead_firstname: dmFirstName || "",
    lead_lastname: dmLastName || "",
    lead_email: contact.lead_email || "",
    lead_telephone: contact.lead_telephone || "",
    lead_address: contact.lead_address || "",
    lead_city: contact.lead_city || "",
    lead_postcode: contact.lead_postcode || "",
    lead_country: contact.lead_country || "",
    lead_website: contact.lead_website || "",
    lead_description: contact.lead_description || "",
    lead_type: contact.lead_type || "",
    lead_instagram: contact.lead_instagram || "",
    lead_linkedin: contact.lead_linkedin || "",
    lead_facebook: contact.lead_facebook || "",
    lead_tiktok: contact.lead_tiktok || "",
    lead_rating: contact.lead_rating != null ? String(contact.lead_rating) : "",
    lead_reviews: contact.lead_reviews != null ? String(contact.lead_reviews) : "",
    // Recipient / sender
    recipient_name: recipient.name || "",
    recipient_role: recipient.role || "",
    user_name: userSettings.user_name || "",
    company_name: userSettings.company_name || "",
    // SEO
    seo_visibility: seoVisibility,
    seo_keywords_total: seoKeywordsTotal,
    seo_keywords_top10: seoKeywordsTop10,
    seo_top_keyword: seoTopKeyword,
    seo_main_competitor: seoMainCompetitor,
    seo_backlinks: seoBacklinks,
    seo_traffic: seoTraffic,
    seo_traffic_value: seoTrafficValue,
    seo_trend: seoTrend,
    // Backward-compat alias
    first_name: dmFirstName || "",
  };
};

/**
 * Format the stored Sistrix SEO analysis (researchIntelligence.seoAnalysis)
 * into a compact multi-line block. Crucially includes the visibility TIME
 * SERIES (visibilityTrend.points) so an image model can actually draw a chart
 * of "visibility over the last year". Mirrors NormalMode's seo context.
 */
const buildSeoContextText = (seo) => {
  if (!seo || !seo.data) return "";
  const d = seo.data;
  const parts = [];

  const core = d.core || {};
  const head = [];
  if (core.visibility?.value != null) head.push(`Visibility index ${Number(core.visibility.value).toFixed(2)}`);
  if (core.keywordsTotal != null) head.push(`${core.keywordsTotal} keywords total`);
  if (core.keywordsTop10 != null) head.push(`${core.keywordsTop10} in Top 10`);
  if (head.length) {
    parts.push(`[SEO ANALYSIS]${seo.country ? ` (Google ${String(seo.country).toUpperCase()})` : ""} ${head.join(" · ")}`);
  }

  const tv = d.trafficValue;
  if (tv?.estimatedTrafficMonthly != null) {
    parts.push(
      `Estimated organic traffic: ~${tv.estimatedTrafficMonthly} visitors/month${tv.estimatedValueMonthly ? ` worth ~€${tv.estimatedValueMonthly}/month` : ""}`
    );
  }

  const vt = d.visibilityTrend;
  if (vt) {
    if (vt.changePct != null) {
      parts.push(
        `Visibility trend: ${vt.direction === "up" ? "GAINING" : vt.direction === "down" ? "LOSING" : "flat"} ${vt.changePct > 0 ? "+" : ""}${vt.changePct}% over ${vt.overWindow || "12M"}`
      );
    }
    if (Array.isArray(vt.points) && vt.points.length) {
      const series = vt.points
        .slice()
        .reverse() // oldest -> newest, so a chart reads left-to-right
        .filter((p) => p && p.value != null)
        .map((p) => `${p.label != null ? p.label : "?"}=${p.value}`);
      if (vt.current != null) series.push(`now=${vt.current}`);
      if (series.length) {
        parts.push(`Visibility over time (oldest→newest data points, use these exact numbers for charts): ${series.join(", ")}`);
      }
    }
  }

  const bl = d.backlinks;
  if (bl?.total != null) parts.push(`Backlinks: ${bl.total}${bl.referringDomains != null ? ` from ${bl.referringDomains} domains` : ""}`);

  const comps = Array.isArray(d.competitors) ? d.competitors.slice(0, 3) : [];
  if (comps.length) {
    parts.push(`Top competitors: ${comps.map((c) => {
      const overlap = c && Number(c.match);
      return c.domain && Number.isFinite(overlap) && overlap > 0 ? `${c.domain} (keyword overlap ${overlap})` : c.domain;
    }).filter(Boolean).join(", ")}`);
  }

  const opps = Array.isArray(d.opportunities) ? d.opportunities.slice(0, 3) : [];
  if (opps.length) parts.push(`Keyword opportunities: ${opps.map((o) => `"${o.keyword}" (pos ${o.position})`).join(", ")}`);

  return parts.join("\n");
};

/** Truncate helper. */
const clip = (val, max) => {
  const s = String(val == null ? "" : val).trim();
  return s.length > max ? s.slice(0, max) + "…" : s;
};

/**
 * Build a compact recipient context string that the image model can use
 * (business description, SEO signals, notes). Bounded to stay cheap.
 */
export const buildImageContextText = (contact) => {
  if (!contact || typeof contact !== "object") return "";
  const ri = contact.researchIntelligence || {};
  const lines = [];

  if (contact.business_name) lines.push(`Business: ${contact.business_name}`);
  if (contact.lead_type) lines.push(`Industry: ${contact.lead_type}`);
  if (contact.lead_city || contact.lead_country) {
    lines.push(`Location: ${[contact.lead_city, contact.lead_country].filter(Boolean).join(", ")}`);
  }

  // Description without the appended [SEO]/[IG Research] research blocks —
  // the structured SEO context below is richer and avoids duplication.
  if (contact.lead_description) {
    const desc = String(contact.lead_description)
      .replace(/\n?\[SEO\].*$/s, "")
      .replace(/\n?\[IG Research\].*$/s, "")
      .trim();
    if (desc) lines.push(`Description: ${clip(desc, 600)}`);
  }

  const seoText = buildSeoContextText(ri.seoAnalysis);
  if (seoText) lines.push(seoText);

  // Outreach notes (free-text context the user curated).
  if (Array.isArray(contact.lead_notes)) {
    const noteText = contact.lead_notes
      .filter((n) => n && (n.type === "outreach" || !n.type) && n.text)
      .map((n) => n.text)
      .join(" · ");
    if (noteText) lines.push(`Notes: ${clip(noteText, 500)}`);
  }

  return clip(lines.join("\n"), 3800);
};

/**
 * Generate (or fetch cached) AI image for one contact + token.
 *
 * Runs behind the concurrency gate. `priority` decides who goes first when more
 * than `MAX_CONCURRENT_IMAGE_GENERATIONS` are pending; `shouldSkip` is checked
 * once the slot is granted, so a job that became obsolete while queued is
 * dropped for free instead of being billed.
 *
 * @returns {Promise<{imageUrl, altText, maxWidth, align, token, cached}>}
 */
export const generateAiImageForContact = async ({ userId, token, contactId, contact, userSettings, selectedEmail, force = false, refinePrompt = "", refineImageUrl = "", priority = IMAGE_PRIORITY_ACTIVE, shouldSkip }) => {
  const variables = buildImagePromptVariables(contact, { userSettings, selectedEmail });
  const contextText = buildImageContextText(contact);

  await acquireImageSlot(priority, imageJobKey(contactId, token));
  try {
    if (typeof shouldSkip === "function" && shouldSkip()) {
      const skipped = new Error("Image generation no longer needed");
      skipped.imageSkipped = true;
      throw skipped;
    }

    const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.generateDynamicImage(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, token, contactId: contactId || null, variables, contextText, force, refinePrompt, refineImageUrl }),
    });

    if (!response.ok) {
      let detail = `HTTP ${response.status}`;
      try {
        const err = await response.json();
        detail = err.details || err.error || detail;
      } catch (_) {
        /* ignore */
      }
      throw new Error(detail);
    }

    const data = await response.json();
    if (data.empty) {
      throw new Error(data.message || "No verified chances in this window. This email goes out without the card.");
    }
    return data;
  } finally {
    releaseImageSlot();
  }
};

/**
 * Merge per-recipient generated image URLs into a copy of userSettings so that
 * convertToHtml/resolveImageTokens resolves AI tokens to the freshly generated
 * image. `generatedImages` is a map token -> { imageUrl, altText, maxWidth, align }.
 */
export const withResolvedAiImages = (userSettings, generatedImages) => {
  if (!generatedImages || Object.keys(generatedImages).length === 0) return userSettings;
  const base = userSettings && typeof userSettings === "object" ? userSettings : {};
  const baseAssets = base.imageLabAssets && typeof base.imageLabAssets === "object" ? base.imageLabAssets : {};
  const mergedAssets = { ...baseAssets };
  Object.entries(generatedImages).forEach(([token, gen]) => {
    if (!gen || !gen.imageUrl) return;
    const existing = baseAssets[token] || {};
    mergedAssets[token] = {
      ...existing,
      token,
      imageUrl: gen.imageUrl,
      altText: gen.altText != null ? gen.altText : existing.altText || "",
      maxWidth: gen.maxWidth || existing.maxWidth || 600,
      align: gen.align || existing.align || "center",
      ...(gen.linkUrl ? { linkUrl: gen.linkUrl, width: gen.width, height: gen.height } : {}),
    };
  });
  return { ...base, imageLabAssets: mergedAssets };
};

/**
 * Substitute {{variable}} tokens in a string. Mirrors the backend
 * `applyVariables` exactly: recognized keys are always replaced (empty string
 * when missing) so no raw `{{lead_x}}` leaks; unknown keys are left untouched.
 * The `:` in `{{image:token}}` excludes it from the key charset, so image
 * placeholders are never affected.
 */
export const applyImageVariables = (text, variables) => {
  if (!text || typeof text !== "string") return "";
  const vars = variables && typeof variables === "object" ? variables : {};
  return text.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (full, key) => {
    if (Object.prototype.hasOwnProperty.call(vars, key)) {
      const val = vars[key];
      return val == null ? "" : String(val);
    }
    return full;
  });
};

/**
 * Return a userSettings copy whose Image Lab asset alt texts are resolved
 * against a recipient's variables. Ensures every send gets a company-specific,
 * non-identical `alt` attribute (better deliverability, no placeholder leaks).
 * Safe to call on already-resolved alts (no {{}} left → no-op).
 */
export const withResolvedImageAltText = (userSettings, variables) => {
  const base = userSettings && typeof userSettings === "object" ? userSettings : {};
  const assets = base.imageLabAssets;
  if (!assets || typeof assets !== "object" || !variables) return base;
  const resolved = {};
  Object.entries(assets).forEach(([token, asset]) => {
    if (asset && typeof asset === "object" && asset.altText) {
      resolved[token] = { ...asset, altText: applyImageVariables(asset.altText, variables) };
    } else {
      resolved[token] = asset;
    }
  });
  return { ...base, imageLabAssets: resolved };
};
